import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { api } from "../../api/api";
import { tr } from "../../i18n";
import { StatusBadge } from "./StatsParts";
import { formatDateTime } from "./statsFormat";

const TYPES = [
  { value: "", label: "All events" },
  { value: "offline", label: "Offline" },
  { value: "online", label: "Online" },
  { value: "self_heal", label: "Self-heal" },
  { value: "load_error", label: "Load error" },
];

/** Internal: the status events the staff alert emails are built from. */
export default function AlertsTab({ contextUserId, onOpenScreen }) {
  const [days, setDays] = useState(7);
  const [type, setType] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!contextUserId) return;
    let active = true;
    setData(null);
    setError("");
    api
      .get(`/kpi/alerts/${contextUserId}`, { params: { days, type: type || undefined } })
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setError(e?.response?.data?.message || tr("Could not load alerts.")); });
    return () => { active = false; };
  }, [contextUserId, days, type]);

  return (
    <div className="stats-tab">
      <div className="stats-toolbar">
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, v) => v && setDays(v)} aria-label={tr("Period")}>
          {[1, 7, 30].map((d) => (
            <ToggleButton key={d} value={d}>{d === 1 ? tr("24 h") : tr("{n} days", { n: d })}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField select size="small" label={tr("Type")} value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 170 }}>
          {TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>{tr(t.label)}</MenuItem>
          ))}
        </TextField>
        <span className="stats-muted">
          {tr("Offline alerts are emailed after 10 minutes; self-heals after 3 in 24 h. Never shown to customers.")}
        </span>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>}

      {data && (
        <Paper elevation={0} className="stats-card">
          {data.events.length === 0 ? (
            <p className="stats-muted">{tr("No events in this period.")}</p>
          ) : (
            <Table size="small" className="stats-table">
              <TableHead>
                <TableRow>
                  <TableCell>{tr("Time")}</TableCell>
                  <TableCell>{tr("Event")}</TableCell>
                  <TableCell>{tr("Customer")}</TableCell>
                  <TableCell>{tr("Screen")}</TableCell>
                  <TableCell className="stats-hide-mobile">{tr("Detail")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.events.map((e) => (
                  <TableRow
                    key={e.id}
                    hover={Boolean(e.screenId)}
                    onClick={() => e.screenId && onOpenScreen(e.screenId)}
                    sx={{ cursor: e.screenId ? "pointer" : "default" }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(e.at)}</TableCell>
                    <TableCell><StatusBadge kind={e.status} /></TableCell>
                    <TableCell>{e.customer || "–"}</TableCell>
                    <TableCell>{e.screenName || e.mac}</TableCell>
                    <TableCell className="stats-hide-mobile stats-detail-cell">{e.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {data.events.length >= 500 && <p className="stats-muted">{tr("Showing the latest 500 events.")}</p>}
        </Paper>
      )}
    </div>
  );
}
